import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { selectLocale } from '@/app/slices/appSlice'
import { selectCurrencyCode, selectOrganisationId, selectUseImperialUnits } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { formatNumber } from '@/common/helpers/formatUtilities'
import { useOrderItemPropertyOptions } from '@/common/hooks/useOrderItemPropertyOptions'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { getCurrencyFormat, getPartDimensions } from '@/common/utils'

const PartSettings = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId } = useParams()

    const { showGrainDirection } = useToolBoxTreatments()

    const locale = useSelector(selectLocale)
    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const currencyCode = useSelector(selectCurrencyCode)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [updateOrderItem] = useUpdateOrderItemMutation()

    const [itemCuttingTechnology, setItemCuttingTechnology] = useState(orderItem?.cuttingTechnologyId || '')
    const [itemMaterial, setItemMaterial] = useState(orderItem?.materialId || '')
    const [itemThickness, setItemThickness] = useState(orderItem?.thickness || '')
    const [itemGrainDirection, setItemGrainDirection] = useState(orderItem?.grainDirection || 'None')
    const [itemQuantity, setItemQuantity] = useState(orderItem?.quantity || 1)
    const [quantityError, setQuantityError] = useState(false)

    const partDimensions = useMemo(() => getPartDimensions(orderItem), [orderItem])
    const units = useMemo(() => (useImperialUnits ? 'in' : 'mm'), [useImperialUnits])
    const mode = useMemo(() => ('profile' in orderItem ? 'rotary' : 'flat'), [orderItem])

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)
    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const { cuttingTechOptions, error, grainDirectionOptions, materialOptions, thicknessOptions } =
        useOrderItemPropertyOptions({
            cuttingTechnologyId: itemCuttingTechnology,
            materialId: itemMaterial,
            thickness: itemThickness,
            mode,
            partDimensions,
        })

    const moreThanOneCuttingTech = cuttingTechOptions?.length > 1

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (_error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateOrderItem = useDebouncedCallback(handleUpdateOrderItem, 800)

    const handleCuttingTechChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemCuttingTechnology !== value) {
            setItemCuttingTechnology(value)
            setItemMaterial('')
            setItemThickness('')

            await handleUpdateOrderItem({
                ...orderItem,
                cuttingTechnologyId: value,
                materialId: '',
                thickness: '',
                sheetId: '',
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
            })
        }
    }

    const handleMaterialChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemMaterial !== value) {
            setItemMaterial(value)
            setItemThickness('')

            await handleUpdateOrderItem({
                ...orderItem,
                materialId: value,
                thickness: '',
                sheetId: '',
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
            })
        }
    }

    const handleThicknessChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemThickness !== value) {
            setItemThickness(value)

            await handleUpdateOrderItem({
                ...orderItem,
                thickness: value,
                sheetId: '',
                materialConsumptionMode: '',
                isCustomerSuppliedMaterial: '',
            })
        }
    }

    const handleGrainDirectionChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemGrainDirection !== value) {
            setItemGrainDirection(value)
            await handleUpdateOrderItem({
                ...orderItem,
                grainDirection: value,
            })
        }
    }

    const handleQuantityChange = async (values) => {
        const { floatValue } = values
        setQuantityError(!floatValue || isNaN(floatValue) || floatValue <= 0)

        setItemQuantity(floatValue)

        if (!isNaN(floatValue) || floatValue >= 1) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                quantity: floatValue,
            })
        }
    }

    useEffect(() => {
        setItemQuantity(orderItem.quantity)
    }, [orderItem?.quantity])

    useEffect(() => {
        setItemCuttingTechnology(orderItem?.cuttingTechnologyId || '')
    }, [orderItem?.cuttingTechnologyId])

    useEffect(() => {
        setItemMaterial(orderItem?.materialId || '')
    }, [orderItem?.materialId])

    useEffect(() => {
        setItemThickness(orderItem?.thickness || '')
    }, [orderItem?.thickness])

    useEffect(() => {
        setItemGrainDirection(orderItem?.grainDirection || 'None')
    }, [orderItem?.grainDirection])

    if (error) {
        return <Typography color="error">{t('Error loading settings. Please try again.')}</Typography>
    }

    return (
        <Box key={orderItemId}>
            {/* CUTTING TECH */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Cutting technology')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            id="select-itemCuttingTechnology"
                            inputProps={{ 'data-testid': 'order-item-cutting-technology' }}
                            labelId="select-itemCuttingTechnology-label"
                            readOnly={!moreThanOneCuttingTech || isPartFromLibrary}
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                            }}
                            value={itemCuttingTechnology}
                            fullWidth
                            onChange={handleCuttingTechChange}
                        >
                            {cuttingTechOptions
                                ? cuttingTechOptions?.map((ct) => (
                                      <MenuItem
                                          key={ct.cuttingTechnologyId}
                                          value={ct.cuttingTechnologyId}
                                      >
                                          {ct.name}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>

            {/* MATERIAL */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Material')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={
                        isPartFromLibrary
                            ? itemMaterial == '' || itemThickness == ''
                                ? t('Material for Part Library Part is no longer available for selection')
                                : t('Cannot edit parts from library')
                            : ''
                    }
                    arrow
                >
                    <Box
                        sx={{
                            flex: 3,
                        }}
                    >
                        <Select
                            disabled={!itemCuttingTechnology || !materialOptions?.length || isPartFromLibrary}
                            id="select-itemMaterial"
                            inputProps={{ 'data-testid': 'order-item-material' }}
                            labelId="select-itemMaterial-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                            }}
                            value={itemMaterial}
                            fullWidth
                            onChange={handleMaterialChange}
                        >
                            {materialOptions
                                ? materialOptions?.map((material) => (
                                      <MenuItem
                                          key={material.materialId}
                                          value={material.materialId}
                                      >
                                          {material.materialName}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>

            {/* THICKNESS */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Thickness')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={
                        isPartFromLibrary
                            ? itemMaterial == '' || itemThickness == ''
                                ? t('Material for Part Library Part is no longer available for selection')
                                : t('Cannot edit parts from library')
                            : ''
                    }
                    arrow
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Select
                            disabled={!itemMaterial || !thicknessOptions?.length || isPartFromLibrary}
                            id="select-itemThickness"
                            inputProps={{ 'data-testid': 'order-item-thickness' }}
                            labelId="select-itemThickness-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                            }}
                            value={itemThickness}
                            fullWidth
                            onChange={handleThicknessChange}
                        >
                            {thicknessOptions
                                ? thicknessOptions?.map((thickness) => (
                                      <MenuItem
                                          key={thickness}
                                          sx={{ justifyContent: 'flex-end' }}
                                          value={thickness}
                                      >
                                          {formatNumber(thickness, locale, useImperialUnits)} {units}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>

            {/* GRAIN DIRECION */}
            {showGrainDirection && !('profile' in orderItem) ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'space-between',
                        margin: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography
                            color="text.secondary"
                            variant="small"
                        >
                            {t('Grain direction')}
                        </Typography>
                    </Box>
                    <TbxTooltip
                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                        arrow
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <Select
                                disabled={isPartFromLibrary}
                                id="select-itemGrainDirection"
                                inputProps={{ 'data-testid': 'order-item-grain-direction' }}
                                labelId="select-itemGrainDirection-label"
                                SelectDisplayProps={{ style: { textAlign: 'right' } }}
                                size="small"
                                sx={{
                                    '& .MuiSelect-select': {
                                        fontSize: '14px',
                                        paddingBlock: '5px',
                                        paddingInline: '12px',
                                    },
                                }}
                                value={itemGrainDirection}
                                fullWidth
                                onChange={handleGrainDirectionChange}
                            >
                                {grainDirectionOptions.map((direction) => (
                                    <MenuItem
                                        key={`direction-${direction.value}`}
                                        value={direction.value}
                                    >
                                        {direction.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </TbxTooltip>
                </Box>
            ) : null}

            {/* QUANTITY */}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Quantity')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalSeparator={currencyFormat.decimal}
                        error={quantityError}
                        helperText={quantityError ? t('Quantity cannot be less than 1') : null}
                        placeholder="0"
                        size="small"
                        slotProps={{
                            input: {
                                style: { fontSize: '14px' },
                            },
                            htmlInput: {
                                style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                                'data-testid': 'order-item-quantity',
                            },
                        }}
                        thousandSeparator={currencyFormat.group}
                        value={itemQuantity}
                        variant="outlined"
                        onValueChange={handleQuantityChange}
                    />
                </Box>
            </Box>
        </Box>
    )
}

PartSettings.propTypes = {
    isPartFromLibrary: PropTypes.bool,
    orderItemId: PropTypes.string,
}

export default PartSettings
